import React, { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import GeneratingLoader from './GeneratingLoader';
// ─────────────────────────────────────────────
// SIGNATURE PAD
// ─────────────────────────────────────────────
function SignaturePad({ onDone }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [mode, setMode] = useState('draw');
  const lastPos = useRef(null);
  const currentDataUrl = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1c1b18';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setHasSignature(false);
    currentDataUrl.current = null;
  }, [mode]);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) return {
      x: (e.touches[0].clientX - rect.left) * scaleX,
      y: (e.touches[0].clientY - rect.top) * scaleY,
    };
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPos.current = getPos(e, canvasRef.current);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setHasSignature(true);
    currentDataUrl.current = canvas.toDataURL('image/png');
  };

  const stopDraw = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    currentDataUrl.current = null;
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const x = (canvas.width - img.width * scale) / 2;
        const y = (canvas.height - img.height * scale) / 2;
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        setHasSignature(true);
        currentDataUrl.current = canvas.toDataURL('image/png');
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="sig-pad-wrapper">
      <div className="sig-pad-tabs">
        <button className={`sig-tab ${mode === 'draw' ? 'active' : ''}`} onClick={() => setMode('draw')} type="button">Draw</button>
        <button className={`sig-tab ${mode === 'upload' ? 'active' : ''}`} onClick={() => setMode('upload')} type="button">Upload</button>
      </div>
      <div className="sig-canvas-area">
        <canvas
          ref={canvasRef} width={500} height={120} className="sig-canvas"
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
          style={{ display: mode === 'draw' ? 'block' : 'none' }}
        />
        {mode === 'draw' && !hasSignature && <div className="sig-placeholder">Sign here</div>}
        {mode === 'upload' && (
          <label className="sig-upload-label">
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
            {!hasSignature ? (
              <div className="sig-upload-prompt">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Click to upload</span>
                <span className="sig-upload-hint">PNG, JPG or SVG</span>
              </div>
            ) : (
              <div className="sig-upload-prompt">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <polyline points="20 6 9 17 4 12" stroke="#2e7d32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span style={{ color: '#2e7d32' }}>Image loaded</span>
              </div>
            )}
          </label>
        )}
      </div>
      <div className="sig-pad-actions">
        {hasSignature && (
          <button className="sig-clear-btn" onClick={clearCanvas} type="button">Clear</button>
        )}
        <button
          className="sig-done-btn"
          onClick={() => onDone(currentDataUrl.current)}
          type="button"
          disabled={!hasSignature}
        >
          Save Signature
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// EMAIL SCRIPT
// ─────────────────────────────────────────────
function EmailScript({ letter }) {
  const [copiedField, setCopiedField] = useState(null);

  const agentEmail = letter.knownAgent?.email || null;
  const today = new Date(letter.timestamp).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  const subject = `DMCA Takedown Notice — ${letter.formData.platform} — ${today}`;

  const body =
`Dear ${letter.knownAgent ? letter.knownAgent.agent : letter.formData.platform + ' Legal Team'},

Please find attached my DMCA takedown notice regarding unauthorized use of my copyrighted work on your platform, prepared under 17 U.S.C. § 512(c)(3).

Please remove or disable access to the infringing content at:
${letter.formData.infringingUrl}

Please confirm receipt and action taken.

Sincerely,
${letter.formData.claimantName}
${letter.formData.claimantEmail}`;

  const copy = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const CopyBtn = ({ field, text }) => (
    <button className="email-copy-btn" onClick={() => copy(text, field)}>
      {copiedField === field ? (
        <>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
            <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          Copied
        </>
      ) : (
        <>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
            <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Copy
        </>
      )}
    </button>
  );

  return (
    <div className="email-script-section">
      <div className="email-script-header">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <polyline points="22,6 12,13 2,6"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Send via email</span>
      </div>

      <p className="email-script-instruction">
        Download the PDF above, then send it to the agent email below using this ready-to-send script.
      </p>

      {/* TO */}
      <div className="email-field-row">
        <span className="email-field-label">To</span>
        <span className="email-field-value">
          {agentEmail || '[Find platform DMCA email and paste here]'}
        </span>
        {agentEmail && <CopyBtn field="email" text={agentEmail} />}
      </div>

      {/* SUBJECT */}
      <div className="email-field-row">
        <span className="email-field-label">Subject</span>
        <span className="email-field-value email-field-subject">{subject}</span>
        <CopyBtn field="subject" text={subject} />
      </div>

      {/* BODY */}
      <div className="email-body-wrap">
        <div className="email-body-header">
          <span className="email-field-label">Body</span>
          <CopyBtn field="body" text={body} />
        </div>
        <pre className="email-body-text">{body}</pre>
      </div>

      <div className="email-script-note">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        Attach the downloaded PDF before sending.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// RESULTS
// ─────────────────────────────────────────────
export function DMCAResults({ isProcessing, generatedLetters, user, deleteLetter, onNewGeneration }) {
  const [copiedId, setCopiedId]           = useState(null);
  const [copyToast, setCopyToast]         = useState(false);
  const [editingId, setEditingId]         = useState(null);
  const [editedTexts, setEditedTexts]     = useState({});
  const [signatureData, setSignatureData] = useState({});
  const [showSigPad, setShowSigPad]       = useState({});

  const SIG_W = 160;
  const SIG_H = 55;

  const getLetterText = (letter) => {
    const text = editedTexts[letter.id] !== undefined
      ? editedTexts[letter.id]
      : letter.letterText;
    if (signatureData[letter.id]) {
      return text.replace(/\[SIGNATURE\]/gi, '').trim();
    }
    return text;
  };

  const copyLetter = (letter) => {
    navigator.clipboard.writeText(getLetterText(letter)).then(() => {
      setCopiedId(letter.id); setCopyToast(true);
      setTimeout(() => { setCopyToast(false); setCopiedId(null); }, 2200);
    });
  };

  const downloadPDF = (letter) => {
    const doc     = new jsPDF({ unit: 'mm', format: 'a4' });
    const margin  = 25;
    const pageW   = doc.internal.pageSize.getWidth();
    const pageH   = doc.internal.pageSize.getHeight();
    const maxW    = pageW - margin * 2;
    const lineH   = 7;
    const paraGap = 4;
    let y = margin;

    const checkPage = (needed = lineH) => {
      if (y + needed > pageH - margin) { doc.addPage(); y = margin; }
    };
    const writeParagraph = (text, gap = paraGap) => {
      doc.setFont('times', 'normal'); doc.setFontSize(11);
      doc.splitTextToSize(text.trim(), maxW).forEach(line => {
        checkPage(); doc.text(line, margin, y); y += lineH;
      });
      y += gap;
    };
    const writeBlankLine = (n = 1) => { y += lineH * n; };

    let raw = getLetterText(letter).replace(/\[SIGNATURE\]/gi, '').trim();
    const sincIdx = raw.search(/\bSincerely[,:]?\s*/i);
    let body = raw, afterSincerely = '';
    if (sincIdx !== -1) {
      body = raw.slice(0, sincIdx).trim();
      afterSincerely = raw.slice(sincIdx).replace(/^Sincerely[,:]?\s*/i, '').trim();
    }
    const printedName = afterSincerely || letter.formData?.claimantName || '';

    body.split(/\n{2,}/).forEach(para => {
      const trimmed = para.trim();
      if (!trimmed) return;
      const subLines = trimmed.split('\n');
      if (subLines.length > 1) {
        subLines.forEach(line => {
          const t = line.trim();
          if (!t) { y += lineH * 0.5; return; }
          checkPage();
          doc.setFont('times', 'normal'); doc.setFontSize(11);
          doc.splitTextToSize(t, maxW).forEach(wl => { checkPage(); doc.text(wl, margin, y); y += lineH; });
        });
        y += paraGap;
      } else {
        writeParagraph(trimmed);
      }
    });

    checkPage(lineH * 2);
    writeParagraph('Sincerely,');
    if (printedName) {
      // Reduced gap between "Sincerely," and the printed name
      checkPage();
      doc.setFont('times', 'normal'); doc.setFontSize(11);
      doc.text(printedName, margin, y); y += lineH;
    }

    // FIX: signature image first, line below it — not above
    const sig    = signatureData[letter.id];
    const sigWmm = SIG_W * 0.2646;
    const sigHmm = SIG_H * 0.2646;
    checkPage(sigHmm + 8);

    if (sig) {
      // Draw signature image, then line underneath
      try {
        doc.addImage(sig, 'PNG', margin, y, sigWmm, sigHmm);
        y += sigHmm + 2;
      } catch (_) {}
      doc.setDrawColor(180); doc.setLineWidth(0.4);
      doc.line(margin, y, margin + sigWmm, y);
      y += 6;
    } else {
      // No signature: line first, then hint text below
      doc.setDrawColor(140); doc.setLineWidth(0.4);
      doc.line(margin, y + 4, margin + 60, y + 4);
      y += 8;
      doc.setFontSize(8); doc.setFont('times', 'italic'); doc.setTextColor(160, 160, 160);
      doc.text('Signature', margin, y);
      doc.setTextColor(0, 0, 0);
    }

    doc.save(`DMCA-Notice-${letter.formData.platform}-${Date.now()}.pdf`);
  };

  const fmt = (iso) => new Date(iso).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <div className="results-page">
      {copyToast && (
        <div className="toast-message">
          <span className="toast-dot" />Copied to clipboard
        </div>
      )}

      <div className="results-container">
        <div className="prompt-display">
          <div className="prompt-avatar">
            {user?.email
              ? <span className="user-initial">{user.email.charAt(0).toUpperCase()}</span>
              : <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                </svg>
            }
          </div>
          <span className="prompt-text">
            {generatedLetters[0]
              ? `DMCA takedown notice for ${generatedLetters[0].formData.platform} — "${generatedLetters[0].formData.workDescription}"`
              : 'Generating your DMCA notice…'}
          </span>
        </div>

        {isProcessing ? <GeneratingLoader isProcessing={isProcessing} /> : generatedLetters.length > 0 ? (
          <div className="qr-cards-container">
            {generatedLetters.map((letter, i) => (
              <div key={letter.id} className="qr-card" style={{ animationDelay: `${i * 0.07}s` }}>

                {/* ── LEFT (visually) — Notice Details panel ── */}
                <div className="qr-card-right">
                  <div className="qr-controls">
                    {/* ── WHAT TO DO NEXT ── */}
                    <div className="next-steps-guide">
                      {[
                        {
                          n: '1',
                          icon: (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ),
                          text: 'Add your signature, then click Download PDF.',
                        },
                        {
                          n: '2',
                          icon: (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ),
                          text: 'Copy the email script below the letter.',
                        },
                        {
                          n: '3',
                          icon: (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                              <line x1="22" y1="2" x2="11" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              <polygon points="22 2 15 22 11 13 2 9 22 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ),
                          text: letter.knownAgent?.email
                            ? <>Send to <strong style={{ color: '#eeecea', fontWeight: 600 }}>{letter.knownAgent.email}</strong> with the PDF attached.</>
                            : 'Find the platform\'s DMCA email, attach the PDF, and send.',
                        },
                      ].map(({ n, icon, text }) => (
                        <div className="nsg-step" key={n}>
                          <div className="nsg-num">{n}</div>
                          <div className="nsg-icon">{icon}</div>
                          <div className="nsg-text">{text}</div>
                        </div>
                      ))}
                    </div>

                    {/* SIGNATURE */}
                    <div className="right-sig-section">
                      <div className="right-sig-header">
                        <div className="right-sig-title-row">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span className='sign-header'>Signature</span>
                          {signatureData[letter.id] && (
                            <span className="sig-ready-badge-inline">✓ Added</span>
                          )}
                        </div>
                        {!showSigPad[letter.id] && (
                          <button
                            className="sig-toggle-btn"
                            type="button"
                            onClick={() => setShowSigPad(prev => ({ ...prev, [letter.id]: true }))}
                          >
                            {signatureData[letter.id] ? 'Change' : 'Add'}
                          </button>
                        )}
                      </div>
                      {showSigPad[letter.id] && (
                        <SignaturePad
                          onDone={(data) => {
                            if (data) setSignatureData(prev => ({ ...prev, [letter.id]: data }));
                            setShowSigPad(prev => ({ ...prev, [letter.id]: false }));
                          }}
                        />
                      )}
                      {signatureData[letter.id] && !showSigPad[letter.id] && (
                        <button
                          className="sig-remove-btn"
                          onClick={() => setSignatureData(prev => {
                            const n = { ...prev }; delete n[letter.id]; return n;
                          })}
                        >
                          Remove signature
                        </button>
                      )}
                      {!signatureData[letter.id] && !showSigPad[letter.id] && (
                        <div className="sig-empty-state">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <p>No signature yet</p>
                          <span>PDF will show a blank line</span>
                        </div>
                      )}

                      <div className="dmca-compliance-badge">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                        </svg>
                        17 U.S.C. § 512 compliant
                      </div>
                    </div>

                    {/* NOTICE DETAILS */}
                    <div className="qr-control-header">
                      <div className="qr-number">Notice Details</div>
                    </div>

                    <div className="dmca-info-list">
                      {[
                        ['Claimant',  letter.formData.claimantName],
                        ['Work type', letter.formData.workType],
                        ['Platform',  letter.formData.platform],
                        ['Generated', fmt(letter.timestamp)],
                      ].map(([label, val]) => (
                        <div className="dmca-info-item" key={label}>
                          <span className="dmca-info-label">{label}</span>
                          <span className="dmca-info-value">{val}</span>
                        </div>
                      ))}
                      {letter.knownAgent && (
                        <div className="dmca-info-item">
                          <span className="dmca-info-label">DMCA agent</span>
                          <span className="dmca-info-value" style={{ fontSize: 12 }}>
                            {letter.knownAgent.agent}
                          </span>
                        </div>
                      )}
                      <div className="dmca-info-item">
                        <span className="dmca-info-label">Original work</span>
                        <a className="dmca-info-link" href={letter.formData.originalWorkUrl}
                          target="_blank" rel="noreferrer">View original ↗</a>
                      </div>
                      <div className="dmca-info-item">
                        <span className="dmca-info-label">Infringing URL</span>
                        <a className="dmca-info-link dmca-info-link-warn" href={letter.formData.infringingUrl}
                          target="_blank" rel="noreferrer">View page ↗</a>
                      </div>
                    </div>

                    {/* NEW NOTICE */}
                    <button className="btn-secondary dmca-new-btn" onClick={onNewGeneration}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      New notice
                    </button>

                  </div>
                </div>

                {/* ── RIGHT (visually) — Letter/PDF preview ── */}
                <div className="qr-card-left">
                  <div className="letter-hero-header">
                    <div className="letter-badge-row">
                      <span className="letter-badge">DMCA Notice</span>
                      <span className="letter-platform">{letter.formData.platform}</span>
                      <span className="letter-date">{fmt(letter.timestamp)}</span>
                      {letter.knownAgent && (
                        <span className="verified-badge">✓ Verified agent</span>
                      )}
                    </div>
                    <div className="action-buttons">
                      <button className="btn-primary" onClick={() => downloadPDF(letter)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Download PDF
                      </button>
                      <button className="btn-secondary" onClick={() => copyLetter(letter)}>
                        {copiedId === letter.id ? (
                          <>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                              <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Copied
                          </>
                        ) : (
                          <>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                              <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            Copy
                          </>
                        )}
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => setEditingId(editingId === letter.id ? null : letter.id)}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {editingId === letter.id ? 'Done' : 'Edit'}
                      </button>
                    </div>
                  </div>

                  {editingId === letter.id ? (
                    <div className="dmca-edit-area">
                      <textarea
                        className="dmca-edit-textarea"
                        value={editedTexts[letter.id] !== undefined ? editedTexts[letter.id] : letter.letterText}
                        onChange={(e) => setEditedTexts(prev => ({ ...prev, [letter.id]: e.target.value }))}
                      />
                      <div className="dmca-edit-actions">
                        <button className="btn-save-edit" onClick={() => setEditingId(null)}>
                          Save changes
                        </button>
                        <button className="btn-cancel-edit" onClick={() => {
                          setEditedTexts(prev => { const n = { ...prev }; delete n[letter.id]; return n; });
                          setEditingId(null);
                        }}>
                          Discard
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="dmca-letter-preview">
                      <div className="letter-document">
                        <pre className="dmca-letter-text">{getLetterText(letter)}</pre>
                        {signatureData[letter.id] && (
                          <div className="letter-sig-live-preview">
                            <img
                              src={signatureData[letter.id]}
                              alt="Signature"
                              className="letter-sig-img"
                              style={{ width: SIG_W, height: SIG_H, objectFit: 'contain' }}
                            />
                            <div className="letter-sig-line" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* EMAIL SCRIPT — full width below the letter */}
                  <div className="email-section-full">
                    <EmailScript letter={letter} />
                  </div>
                </div>

                {generatedLetters.length > 1 && (
                  <button className="btn-delete-compact" onClick={() => deleteLetter(letter.id)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p>No notices generated yet</p>
          </div>
        )}
      </div>
    </div>
  );
}